import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Eye, Edit, Trash2, Send } from 'lucide-react'
import { format } from 'date-fns'
import type { EmailCampaign } from '../types'

interface CampaignTableProps {
  campaigns: EmailCampaign[]
  onView: (campaign: EmailCampaign) => void
  onEdit: (campaign: EmailCampaign) => void
  onDelete: (campaign: EmailCampaign) => void
  onSend: (campaign: EmailCampaign) => void
}

const statusVariants: Record<string, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-gray-50 text-gray-700 border-gray-200' },
  sending: { label: 'Sending', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  completed: { label: 'Completed', className: 'bg-green-50 text-green-700 border-green-200' },
  failed: { label: 'Failed', className: 'bg-red-50 text-red-700 border-red-200' },
}

export function CampaignTable({
  campaigns,
  onView,
  onEdit,
  onDelete,
  onSend,
}: CampaignTableProps) {
  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Campaign</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-center">Recipients</TableHead>
            <TableHead className="text-center">Sent</TableHead>
            <TableHead className="text-center">Opened</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="w-32">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {campaigns.map((campaign) => {
            const status = statusVariants[campaign.status] || statusVariants.draft
            const openRate = campaign.sentCount > 0
              ? ((campaign.openedCount / campaign.sentCount) * 100).toFixed(1)
              : '0'

            return (
              <TableRow key={campaign.id}>
                <TableCell>
                  <div>
                    <p className="font-medium">{campaign.name}</p>
                    <p className="text-sm text-muted-foreground truncate max-w-[250px]">
                      {campaign.subject}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={status.className}>
                    {status.label}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">{campaign.recipientCount}</TableCell>
                <TableCell className="text-center">{campaign.sentCount}</TableCell>
                <TableCell className="text-center">
                  <div>
                    <p>{campaign.openedCount}</p>
                    {campaign.sentCount > 0 && (
                      <p className="text-xs text-muted-foreground">{openRate}%</p>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {format(new Date(campaign.createdAt), 'MMM d, yyyy')}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onView(campaign)}
                      title="View campaign"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {campaign.status === 'draft' && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(campaign)}
                          title="Edit campaign"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onSend(campaign)}
                          title="Send campaign"
                          className="text-green-600 hover:text-green-700"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(campaign)}
                          title="Delete campaign"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
          {campaigns.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                No campaigns found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
